// useAutoRefresh composable — 자동 새로고침 interval 관리 (ux-patterns.md §12.1)
import { ref, onMounted, onBeforeUnmount } from 'vue';

// @MX:ANCHOR: [AUTO] 자동 새로고침 composable — 모든 EcountEtl 화면에서 사용
// @MX:REASON: [AUTO] fan_in >= 4 (Dashboard, RunsList, DeadLetters, RunDetail)
// @MX:SPEC: SPEC-ECOUNT-ETL-OPS-CONSOLE-001 REQ-OC-COMMON-003

/**
 * 자동 새로고침 composable
 * @param {() => Promise<void>} fetchFn - 데이터 fetch 함수
 * @param {Object} options
 * @param {number}  options.intervalMs  - 폴링 간격 (기본 30000ms)
 * @param {boolean} options.defaultOn   - 초기 상태 (기본 true)
 * @param {string}  options.storageKey  - sessionStorage 키 (기본 'ecountEtl.autoRefresh')
 * @returns {{ enabled, toggle, lastFetchAt, secondsUntilNext, forceRefresh }}
 */
export function useAutoRefresh(fetchFn, options = {}) {
  const {
    intervalMs = 30000,
    defaultOn = true,
    storageKey = 'ecountEtl.autoRefresh',
  } = options;

  // sessionStorage에서 초기 상태 복원
  const storedVal = sessionStorage.getItem(storageKey);
  const initialEnabled = storedVal !== null ? storedVal === 'true' : defaultOn;

  const enabled = ref(initialEnabled);
  const lastFetchAt = ref(null);
  const secondsUntilNext = ref(0);

  let intervalId = null;
  let countdownId = null;
  let remainingSeconds = 0;

  function startCountdown() {
    remainingSeconds = Math.floor(intervalMs / 1000);
    secondsUntilNext.value = remainingSeconds;
    countdownId = setInterval(() => {
      remainingSeconds = Math.max(0, remainingSeconds - 1);
      secondsUntilNext.value = remainingSeconds;
    }, 1000);
  }

  function stopCountdown() {
    if (countdownId) {
      clearInterval(countdownId);
      countdownId = null;
    }
    secondsUntilNext.value = 0;
  }

  function startInterval() {
    if (intervalId) return;
    intervalId = setInterval(async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        await fetchFn();
        lastFetchAt.value = new Date();
      } catch (_e) {
        // 에러는 호출자 fetchFn 내부에서 처리
      }
      stopCountdown();
      startCountdown();
    }, intervalMs);
    startCountdown();
  }

  function stopInterval() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    stopCountdown();
  }

  function toggle() {
    enabled.value = !enabled.value;
    sessionStorage.setItem(storageKey, String(enabled.value));
    if (enabled.value) {
      startInterval();
    } else {
      stopInterval();
    }
  }

  async function forceRefresh() {
    try {
      await fetchFn();
      lastFetchAt.value = new Date();
    } catch (_e) {
      // 에러는 호출자 fetchFn 내부에서 처리
    }
    // interval이 켜져 있으면 카운트다운 리셋
    if (enabled.value) {
      stopInterval();
      startInterval();
    }
  }

  // visibility 변경 핸들러 — 탭 숨김 시 fetch 중단, 복귀 시 즉시 fetch + 재시작
  // @MX:WARN: [AUTO] visibilitychange 이벤트 removeEventListener 누락 시 메모리 누수
  // @MX:REASON: [AUTO] onBeforeUnmount에서 반드시 제거 필요 (SPA 화면 전환 시 누적)
  async function handleVisibilityChange() {
    if (document.visibilityState === 'visible' && enabled.value) {
      // 탭 복귀 시 즉시 fetch
      try {
        await fetchFn();
        lastFetchAt.value = new Date();
      } catch (_e) {
        // 에러 무시
      }
      stopInterval();
      startInterval();
    }
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (enabled.value) {
      startInterval();
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    stopInterval();
  });

  return { enabled, toggle, lastFetchAt, secondsUntilNext, forceRefresh };
}
